import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import { countsAsRealUsage } from '../utils/analyticsGate';
import { getPerformance } from 'firebase/performance';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { readWebConfig } from './webConfig';
import { emulatorPorts } from './emulatorPorts';


/**
 * REQUIRED, NEVER DEFAULTED (owner 2026-09-13). `authDomain`, `projectId` and `storageBucket` used to fall
 * back to hardcoded PRODUCTION values, so a build that lost its environment came up pointing at production
 * while believing it was staging — a wrong answer that looks like a working app. See `webConfig.ts`.
 */
const firebaseConfig = readWebConfig(process.env as Record<string, string | undefined>);

// Initialize Firebase app
export const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);

// Firestore with a PERSISTENT local cache (IndexedDB): chat history + server state load INSTANTLY
// from the local cache on reload (then live-update via onSnapshot), and work offline — instead of a
// cold re-fetch every time. Multi-tab safe. Falls back to the default in-memory instance if the
// cache can't initialise (a browser without IndexedDB, private mode, or an already-initialised app
// during hot-reload), so a persistence hiccup never breaks the app.
export const db = (() => {
  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      // Auto-detect long-polling: Firestore's default WebChannel streaming transport fails behind some networks,
      // proxies, VPNs, ad-blockers, and the LOCAL EMULATOR — surfacing as repeated `WebChannelConnection RPC
      // 'Listen' stream transport errored` / QUIC 400 console errors, which silently BREAK live onSnapshot listeners
      // (chat messages, the Studio render poll). Auto-detect probes the connection once and falls back to long-polling
      // when streaming won't work, so realtime keeps working instead of failing quietly. The recommended fix for that
      // exact symptom.
      experimentalAutoDetectLongPolling: true,
    });
  } catch {
    return getFirestore(app);
  }
})();

export const functions = getFunctions(app, 'us-central1'); // Explicit region
export const storage = getStorage(app);

// Initialize analytics and performance if enabled
// `countsAsRealUsage()` excludes WebDriver-driven sessions: the deploy-time prerender pass boots this very
// build in headless Chromium, and Firebase Analytics auto-collects page_view/session_start on init — so
// without this every deploy manufactured a burst of fake visitors. See utils/analyticsGate.
export const analytics = process.env.REACT_APP_ENABLE_ANALYTICS === 'true' && countsAsRealUsage()
  ? getAnalytics(app)
  : null;

export const performance = process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true'
  ? getPerformance(app)
  : null;

// Connect to emulators in development.
// Auth stays on real Firebase (staging) so Google sign-in uses the normal Firebase
// Authentication popup — we do NOT connect the Auth emulator. For authed gateway
// calls to verify locally, run the emulators WITHOUT the Auth emulator (see the
// `dev` script) so the functions emulator verifies real staging ID tokens against
// Google rather than expecting emulator-issued tokens.
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
  console.log('[Firebase] Connecting Firestore + Functions to local emulators; Auth uses real Firebase (staging)');
  connectFirestoreEmulator(db, 'localhost', emulatorPorts.firestore);
  connectFunctionsEmulator(functions, 'localhost', emulatorPorts.functions);

  // OPT-IN Auth emulator (default OFF) — for HEADLESS E2E/UI testing only. When on, the functions emulator must
  // also run with the Auth emulator so it verifies emulator-issued tokens (run all three: auth,firestore,functions).
  // Never enabled in a normal dev/prod flow; it exists so Playwright can sign in locally to reach auth-gated pages.
  if (process.env.REACT_APP_USE_AUTH_EMULATOR === 'true') {
    console.log('[Firebase] Connecting Auth to local emulator (E2E/UI testing)');
    connectAuthEmulator(auth, `http://localhost:${emulatorPorts.auth}`, { disableWarnings: true });
    // Flag-gated test-only hook so a headless Playwright run can sign in (the UI login is Google-only). ONLY
    // exists when the auth emulator is on — never in a normal dev/prod build.
    (window as unknown as { __wtTestAuth?: unknown }).__wtTestAuth = {
      signIn: (email: string, password: string) => signInWithEmailAndPassword(auth, email, password),
      // Creates the account on a fresh emulator without the test knowing which port the emulator is on.
      signUp: (email: string, password: string) => createUserWithEmailAndPassword(auth, email, password),
    };
  }

  // Storage emulator is opt-in via separate flag (REACT_APP_USE_STORAGE_EMULATOR=true)
  // In hybrid mode, Storage should use real Firebase Storage so that:
  // 1. Uploaded files persist to the real staging bucket (not lost when emulator stops)
  // 2. Download URLs use proper https:// format (not http://localhost:9199)
  // 3. URLs stored in the database work across all environments
  if (process.env.REACT_APP_USE_STORAGE_EMULATOR === 'true') {
    console.log('[Firebase] Connecting Storage to local emulator');
    connectStorageEmulator(storage, 'localhost', emulatorPorts.storage);
  } else {
    console.log('[Firebase] Storage using real Firebase Storage (staging bucket)');
  }
}

// Admin email constant
export const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'xuwei@wisetree.ai';
export const PRIVILEGED_DOMAIN = process.env.REACT_APP_PRIVILEGED_DOMAIN || 'wisetree.ai';
export const MAX_FILE_SIZE = parseInt(process.env.REACT_APP_MAX_FILE_SIZE || '10485760'); // 10MB default

// Staging-only: restrict login to approved team emails
const STAGING_ALLOWED_EMAILS = [
  'xuwei@wisetree.ai',
  'email.xuwei@gmail.com',
];

export function getStagingAllowedEmails(): string[] {
  return STAGING_ALLOWED_EMAILS;
}